import { useCallback, useEffect, useRef, useState } from 'react'
import { useLiveQuery } from '@tanstack/react-db'
import { Link, useRouter } from '@tanstack/react-router'

import {
  LedgerBadge,
  LedgerTable,
  type LedgerColumn,
  type LedgerServerQuery,
} from '@/components/ui/LedgerTable'
import { StatusHistoryPill } from '@/components/StatusHistoryPill'
import type {
  ArchivedServiceRow,
  ArchivedServiceSortKey,
} from '@/db/domain/archivedServices'
import { archivedServiceSortKeys } from '@/db/domain/archivedServices'
import { ARCHIVED_SERVICE_STATUSES } from '@/db/domain/serviceStatusOrder'
import { formatServiceStatus } from '@/help/serviceStatus'
import { useDb } from '@/lib/dbContext'
import { formatBookedLabel, fromUtcIsoToZaParts } from '@/lib/datetime'
import { formatShortRelativeDateTime } from '@/lib/relativeDateTime'
import { serviceTypeMeta } from '@/screens/ServicesScreen'

const detailsRouteByType = {
  callout: '/callouts/$id',
  remote: '/sessions/$id',
  sitdown: '/sessions/$id',
  job: '/jobs/$id',
} as const

/**
 * Same look as the shared service-ledger columns, but over search rows from
 * the archived_services view: every filter/sort here runs on the server, so
 * select filters use static options and no status history is available.
 */
function buildArchivedColumns(): LedgerColumn<ArchivedServiceRow>[] {
  return [
    {
      key: 'ref',
      label: 'Ref',
      widthClass: 'w-28',
      sortValue: (row) => row.publicId,
      filter: 'text',
      render: (row) => (
        <Link
          to={detailsRouteByType[row.type]}
          params={{ id: row.publicId }}
          title="Open details page"
          className="font-mono text-[12.5px] whitespace-nowrap text-blue-700 hover:underline"
        >
          {row.publicId}
        </Link>
      ),
    },
    {
      key: 'type',
      label: 'Type',
      widthClass: 'w-24',
      sortValue: (row) => row.type,
      filter: 'select',
      filterOptions: (['callout', 'remote', 'sitdown', 'job'] as const).map(
        (type) => ({ value: type, label: serviceTypeMeta[type].label }),
      ),
      render: (row) => (
        <LedgerBadge tone={serviceTypeMeta[row.type].tone}>
          {serviceTypeMeta[row.type].label}
        </LedgerBadge>
      ),
    },
    {
      key: 'customer',
      label: 'Customer',
      widthClass: 'w-44',
      sortValue: (row) => row.customerName.toLowerCase(),
      filter: 'text',
      render: (row) => (
        <div className="overflow-hidden font-medium text-ellipsis whitespace-nowrap">
          {row.customerId ? (
            <Link
              to="/customers/$id"
              params={{ id: row.customerPublicId ?? row.customerId }}
              className="border-b border-blue-200 text-blue-700 hover:border-blue-700 hover:text-blue-800"
            >
              {row.customerName}
            </Link>
          ) : (
            row.customerName
          )}
        </div>
      ),
    },
    {
      key: 'issue',
      label: 'Issue',
      filter: 'text',
      cellClass: 'text-slate-600',
      render: (row) => (
        <div className="overflow-hidden text-ellipsis whitespace-nowrap">
          {row.summary}
        </div>
      ),
    },
    {
      key: 'booked',
      label: 'Booked',
      widthClass: 'w-32',
      sortValue: (row) => row.bookedAt,
      defaultSortDir: 'desc',
      cellClass: 'whitespace-nowrap text-slate-600',
      render: (row) => {
        const { day, time } = fromUtcIsoToZaParts(row.bookedAt)
        const timeLabel = row.type === 'job' || row.isWholeDay ? '' : time
        return formatBookedLabel(day, timeLabel)
      },
    },
    {
      key: 'assignee',
      label: 'Assignee',
      widthClass: 'w-28',
      sortValue: (row) => (row.assigneeName ?? '').toLowerCase(),
      filter: 'text',
      cellClass: 'whitespace-nowrap text-slate-600',
      render: (row) =>
        row.assigneeName ?? <span className="text-slate-400">—</span>,
    },
    {
      key: 'status',
      label: 'Status',
      widthClass: 'w-40',
      sortValue: (row) => row.status,
      filter: 'select',
      // Only the two terminal statuses — an archived service cannot be anything
      // else, and the SQL view behind this page filters on exactly these.
      filterOptions: ARCHIVED_SERVICE_STATUSES.map((status) => ({
        value: status,
        label: formatServiceStatus(status),
      })),
      cellClass: 'whitespace-nowrap',
      render: (row) => (
        <StatusHistoryPill variant="ledger" currentStatus={row.status} />
      ),
    },
    {
      key: 'updated',
      label: 'Updated',
      widthClass: 'w-24',
      sortValue: (row) => row.updatedAt,
      defaultSortDir: 'desc',
      cellClass: 'whitespace-nowrap text-[12.5px] text-slate-400',
      render: (row) =>
        row.updatedAt ? formatShortRelativeDateTime(row.updatedAt) : '—',
    },
  ]
}

export interface ArchivedServicesScreenProps {
  rows: ArchivedServiceRow[]
  totalItems: number
  loading?: boolean
  error?: boolean
  onQueryChange: (query: LedgerServerQuery) => void
  onOpen?: (row: ArchivedServiceRow) => void
  pageSize?: number
}

export function ArchivedServicesScreen({
  rows,
  totalItems,
  loading,
  error,
  onQueryChange,
  onOpen,
  pageSize = 25,
}: ArchivedServicesScreenProps) {
  const columns = buildArchivedColumns()

  return (
    <div className="bg-slate-50 text-slate-900">
      <main className="max-w-container mx-auto px-4 py-6">
        <LedgerTable
          rows={rows}
          columns={columns}
          rowKey={(row) => row.key}
          itemLabelPlural="archived services"
          emptyLabel="Nothing has been archived yet."
          title="Archived"
          description="Invoiced and cancelled services, kept for reference. Search runs against the full archive."
          onRowClick={onOpen}
          defaultSort={{ key: 'updated', dir: 'desc' }}
          initialPageSize={pageSize}
          minTableWidthClass="min-w-[920px]"
          server={{ totalItems, loading, onQueryChange }}
          mobileSearchKey="customer"
          mobileCard={(row) => (
            <div className="space-y-1.5 px-3.5 py-3">
              <div className="flex items-center justify-between gap-2">
                <div className="flex min-w-0 items-center gap-2">
                  <span className="font-mono text-[12px] text-slate-500">
                    {row.publicId}
                  </span>
                  <LedgerBadge tone={serviceTypeMeta[row.type].tone}>
                    {serviceTypeMeta[row.type].label}
                  </LedgerBadge>
                </div>
                <span className="shrink-0 text-[12px] text-slate-400">
                  {row.updatedAt ? formatShortRelativeDateTime(row.updatedAt) : ''}
                </span>
              </div>
              <div className="min-w-0">
                <div className="truncate font-medium text-slate-900">
                  {row.customerName}
                </div>
                <div className="line-clamp-2 text-[13px] leading-snug text-slate-600">
                  {row.summary}
                </div>
              </div>
              <div className="flex items-center justify-between gap-2 pt-0.5">
                <StatusHistoryPill variant="ledger" currentStatus={row.status} />
                <span className="min-w-0 truncate text-right text-[12px] text-slate-500">
                  {row.assigneeName ? `${row.assigneeName} · ` : ''}
                  {(() => {
                    const { day, time } = fromUtcIsoToZaParts(row.bookedAt)
                    const timeLabel =
                      row.type === 'job' || row.isWholeDay ? '' : time
                    return formatBookedLabel(day, timeLabel)
                  })()}
                </span>
              </div>
            </div>
          )}
          actions={
            <Link
              to="/services"
              className="inline-flex h-8 items-center rounded-lg border border-slate-300 bg-white px-3 text-[12.5px] text-slate-600 hover:bg-slate-50"
            >
              ‹ Back to services
            </Link>
          }
        />
        {error ? (
          <p className="mt-2 text-sm text-rose-600">
            Unable to load archived services right now. Adjust the search or try
            again.
          </p>
        ) : null}
      </main>
    </div>
  )
}

function isSortKey(key: string): key is ArchivedServiceSortKey {
  return (archivedServiceSortKeys as readonly string[]).includes(key)
}

/** Column keys whose text inputs should debounce before hitting the server. */
const debouncedFilterKeys = ['ref', 'customer', 'issue', 'assignee']

function textFiltersChanged(
  a: Record<string, string>,
  b: Record<string, string>,
) {
  return debouncedFilterKeys.some((key) => (a[key] ?? '') !== (b[key] ?? ''))
}

export function ArchivedServicesScreenContainer() {
  const db = useDb()
  const router = useRouter()

  // The server search owns membership + order; the row records live in the
  // TanStack DB archived-rows collection, hydrated by each search.
  const [membership, setMembership] = useState<{
    keys: string[]
    totalItems: number
  }>({ keys: [], totalItems: 0 })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  const liveRows = useLiveQuery((q) =>
    q.from({ rows: db.archivedServicesCollection }),
  )

  const lastQueryRef = useRef<LedgerServerQuery | null>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const requestSeqRef = useRef(0)

  const runQuery = useCallback(
    async (query: LedgerServerQuery) => {
      const seq = ++requestSeqRef.current
      setLoading(true)
      try {
        const response = await db.searchArchivedServices({
          filters: query.filters,
          sort:
            query.sort && isSortKey(query.sort.key)
              ? { key: query.sort.key, dir: query.sort.dir }
              : undefined,
          page: query.page,
          pageSize: query.pageSize,
        })
        if (seq !== requestSeqRef.current) return
        setMembership({ keys: response.keys, totalItems: response.totalItems })
        setError(false)
      } catch (err) {
        if (seq !== requestSeqRef.current) return
        console.error('Archived services search failed', err)
        setError(true)
      } finally {
        if (seq === requestSeqRef.current) setLoading(false)
      }
    },
    [db],
  )

  const handleQueryChange = useCallback(
    (query: LedgerServerQuery) => {
      const previous = lastQueryRef.current
      lastQueryRef.current = query
      if (debounceRef.current) clearTimeout(debounceRef.current)

      // Typing in a text filter debounces; sort, page and select changes fire
      // immediately.
      if (previous && textFiltersChanged(previous.filters, query.filters)) {
        debounceRef.current = setTimeout(() => runQuery(query), 300)
      } else {
        runQuery(query)
      }
    },
    [runQuery],
  )

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [])

  const rowsByKey = new Map(
    (liveRows.data ?? []).map((row) => [row.key, row]),
  )
  const rows = membership.keys.flatMap((key) => {
    const row = rowsByKey.get(key)
    return row ? [row] : []
  })

  return (
    <ArchivedServicesScreen
      rows={rows}
      totalItems={membership.totalItems}
      loading={loading}
      error={error}
      onQueryChange={handleQueryChange}
      onOpen={(row) => {
        router.navigate({
          to: detailsRouteByType[row.type],
          params: { id: row.publicId },
        })
      }}
    />
  )
}
